import { CONDITION_LABEL, technologyPhrase } from '@/lib/technographics/condition';
import type { TechnographicSelection } from '@/lib/technographics/types';
import { pluralize } from '@/lib/utils';
import type { AudienceDefinition } from './audience-definition';
import {
  audienceParts,
  PART_SHOWN_IN,
  type AudiencePart,
  type AudiencePartKey,
} from './audience-parts';
import { COMPANY_FILTER_CONFIGS, CONTACT_FILTER_CONFIGS } from './filter-configs';

/**
 * The read-only filter chip's words: `3 filters · Industry, Employee Range, Job Function`.
 *
 * Names the parts `audience-parts.ts` shows in the filter chip: company and contact groups, then
 * technology, then Target Lists and topics. A group counts once with at least one value, not
 * once per value — the design's `1 filter · Industry` holds a single group however many
 * industries it names. The labels are the drawer's own (`filter-configs.ts`), so the chip and
 * the drawer name a group alike.
 *
 * Technology counts one filter per technology and names each with its condition (NPJB-915):
 * `2 filters · Uses E-Signature, Doesn't use Greentech`.
 *
 * Target Lists and signal topics follow, one filter each however many they hold, named by
 * count (NPJB-944): `2 filters · Target list, 3 signal topics`. Counts need nothing loaded.
 */

const LABEL_BY_KEY = new Map<string, string>(
  [...COMPANY_FILTER_CONFIGS, ...CONTACT_FILTER_CONFIGS].map(({ key, label }) => [key, label]),
);

export const NO_FILTERS_LABEL = 'No filters';

/** Past this many, the chip counts technologies per condition instead of naming them. */
const MAX_NAMED_TECHNOLOGIES = 3;

/** A technology's name, from the technology tree; `undefined` while the tree is not read. */
export type TechnologyNameOf = (topicId: string) => string | undefined;

const NO_NAMES: TechnologyNameOf = () => undefined;

const TECHNOLOGY_MODE = {
  technologyUses: 'runs',
  technologyNotUses: 'notRuns',
} as const satisfies Partial<Record<AudiencePartKey, TechnographicSelection['mode']>>;

type TechnologyPart = AudiencePart & { key: keyof typeof TECHNOLOGY_MODE };

const isTechnology = (part: AudiencePart): part is TechnologyPart => part.key in TECHNOLOGY_MODE;

/** Parts the chip names by how many they hold, after technology and in this order. */
const COUNTED_WORDS = new Map<AudiencePartKey, (count: number) => string>([
  ['targetLists', (n) => (n === 1 ? 'Target list' : `${n} target lists`)],
  ['signalTopics', (n) => `${n} signal ${pluralize(n, 'topic', 'topics')}`],
]);

const countedParts = (parts: AudiencePart[]): string[] =>
  [...COUNTED_WORDS].flatMap(([key, words]) => {
    const part = parts.find((p) => p.key === key);
    return part === undefined ? [] : [words(part.ids.length)];
  });

const labelFor = (key: AudiencePartKey): string => LABEL_BY_KEY.get(key) ?? key;

const technologyCount = (mode: TechnographicSelection['mode'], count: number): string =>
  `${CONDITION_LABEL[mode]} ${count} ${count === 1 ? 'technology' : 'technologies'}`;

const idCount = (parts: readonly AudiencePart[]): number =>
  parts.reduce((n, { ids }) => n + ids.length, 0);

/**
 * "Uses E-Signature", "Doesn't use Greentech" — or, when any name is unknown or there are too
 * many to read in a chip, "Uses 4 technologies", so the chip never shows a bare id.
 */
const technologyParts = (parts: TechnologyPart[], nameOf: TechnologyNameOf): string[] => {
  const byMode = parts.map(({ key, ids }) => ({ mode: TECHNOLOGY_MODE[key], ids }));
  const counted = byMode.map(({ mode, ids }) => technologyCount(mode, ids.length));
  if (idCount(parts) > MAX_NAMED_TECHNOLOGIES) return counted;
  const named: string[] = [];
  for (const { mode, ids } of byMode) {
    for (const id of ids) {
      const name = nameOf(id);
      if (name === undefined) return counted;
      named.push(technologyPhrase(mode, name));
    }
  }
  return named;
};

export const filterSummary = (
  definition: AudienceDefinition,
  nameOf: TechnologyNameOf = NO_NAMES,
): string => {
  const shown = audienceParts(definition).filter(({ key }) => PART_SHOWN_IN[key] === 'filterChip');
  const technology = shown.filter(isTechnology);
  const counted = countedParts(shown);
  const groups = shown.filter((part) => !isTechnology(part) && !COUNTED_WORDS.has(part.key));
  const count = groups.length + idCount(technology) + counted.length;
  if (count === 0) return NO_FILTERS_LABEL;
  const words = [
    ...groups.map(({ key }) => labelFor(key)),
    ...technologyParts(technology, nameOf),
    ...counted,
  ];
  const noun = count === 1 ? 'filter' : 'filters';
  return `${count} ${noun} · ${words.join(', ')}`;
};
